import { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  Divider,
  Avatar,
  IconButton,
} from '@mui/material'
import {
  Add,
  DirectionsCar,
  Speed,
  CloudUpload,
  Delete,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'

const STATUS_COLORS = {
  operational: 'success',
  under_maintenance: 'warning',
  out_for_repair: 'info',
  out_of_service: 'error',
  retired: 'default',
}

const VEHICLE_TYPES = [
  { value: 'ambulance', label: 'Ambulance' },
  { value: 'bus', label: 'Bus' },
  { value: 'car', label: 'Car' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'pickup', label: 'Pickup Truck' },
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'other', label: 'Other' },
]

const STATUS_OPTIONS = [
  { value: 'operational', label: 'Operational' },
  { value: 'under_maintenance', label: 'Under Maintenance' },
  { value: 'out_for_repair', label: 'Out for Repair' },
  { value: 'out_of_service', label: 'Out of Service' },
  { value: 'retired', label: 'Retired' },
]

const FUEL_TYPES = [
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
]

const columns = [
  {
    field: 'image',
    headerName: '',
    width: 60,
    renderCell: (value) => (
      <Avatar
        src={value}
        variant="rounded"
        sx={{ width: 40, height: 40 }}
      >
        <DirectionsCar />
      </Avatar>
    ),
  },
  { field: 'vehicle_number', headerName: 'Vehicle #', sortable: true, width: 120 },
  { field: 'registration_number', headerName: 'Reg #', sortable: true, width: 120 },
  { field: 'name', headerName: 'Name', sortable: true },
  {
    field: 'vehicle_type',
    headerName: 'Type',
    sortable: true,
    renderCell: (value) => value?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
  },
  {
    field: 'make',
    headerName: 'Make/Model',
    sortable: true,
    renderCell: (value, row) => `${value || ''} ${row.model || ''}`.trim() || '-',
  },
  { field: 'department_name', headerName: 'Department', sortable: true },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.replace('_', ' ').toUpperCase()}
        color={STATUS_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'current_mileage',
    headerName: 'Mileage',
    sortable: true,
    renderCell: (value) => value ? `${Number(value).toLocaleString()} km` : '-',
  },
]

const initialFormData = {
  registration_number: '',
  name: '',
  vehicle_type: 'car',
  make: '',
  model: '',
  year: new Date().getFullYear(),
  color: '',
  vin: '',
  engine_number: '',
  fuel_type: 'diesel',
  status: 'operational',
  department: '',
  assigned_driver: '',
  purchase_date: '',
  purchase_cost: '',
  supplier: '',
  insurance_company: '',
  insurance_policy_number: '',
  insurance_expiry: '',
  registration_expiry: '',
  inspection_expiry: '',
  current_mileage: 0,
  next_service_mileage: '',
  notes: '',
}

const VehiclesList = () => {
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [isEditing, setIsEditing] = useState(false)

  // Image upload state
  const [imageFile, setImageFile] = useState(null)
  const [imageFile2, setImageFile2] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [imagePreview2, setImagePreview2] = useState(null)

  // Options for dropdowns
  const [departments, setDepartments] = useState([])
  const [users, setUsers] = useState([])
  const [suppliers, setSuppliers] = useState([])

  const fetchVehicles = async () => {
    try {
      setLoading(true)
      const response = await api.get('/assets/vehicles/')
      setVehicles(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      toast.error('Failed to load vehicles')
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [deptsRes, usersRes, suppliersRes] = await Promise.all([
        api.get('/common/departments/'),
        api.get('/common/users/'),
        api.get('/vendors/'),
      ])
      setDepartments(deptsRes.data.results || deptsRes.data)
      setUsers(usersRes.data.results || usersRes.data)
      setSuppliers(suppliersRes.data.results || suppliersRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  useEffect(() => {
    fetchVehicles()
    fetchOptions()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e, imageNumber) => {
    const file = e.target.files[0]
    if (file) {
      if (imageNumber === 1) {
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
      } else {
        setImageFile2(file)
        setImagePreview2(URL.createObjectURL(file))
      }
    }
  }

  const handleRemoveImage = (imageNumber) => {
    if (imageNumber === 1) {
      setImageFile(null)
      setImagePreview(null)
    } else {
      setImageFile2(null)
      setImagePreview2(null)
    }
  }

  const resetImageState = () => {
    setImageFile(null)
    setImageFile2(null)
    setImagePreview(null)
    setImagePreview2(null)
  }

  const handleSubmit = async () => {
    const newErrors = {}
    if (!formData.registration_number) newErrors.registration_number = 'Registration number is required'
    if (!formData.name) newErrors.name = 'Name is required'
    if (!formData.make) newErrors.make = 'Make is required'
    if (!formData.model) newErrors.model = 'Model is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setFormLoading(true)
    try {
      // Use FormData for multipart/form-data (required for image uploads)
      const submitData = new FormData()

      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          submitData.append(key, value)
        }
      })

      // Handle nullable fields
      const nullableFields = ['department', 'assigned_driver', 'supplier', 'purchase_cost',
        'next_service_mileage', 'purchase_date', 'insurance_expiry', 'registration_expiry', 'inspection_expiry']
      nullableFields.forEach(field => {
        if (!formData[field]) {
          submitData.delete(field)
        }
      })

      // Add image files if selected
      if (imageFile) {
        submitData.append('image', imageFile)
      }
      if (imageFile2) {
        submitData.append('image_2', imageFile2)
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      if (isEditing && selectedVehicle) {
        await api.patch(`/assets/vehicles/${selectedVehicle.id}/`, submitData, config)
      } else {
        await api.post('/assets/vehicles/', submitData, config)
      }

      fetchVehicles()
      setFormOpen(false)
      setFormData(initialFormData)
      resetImageState()
      setIsEditing(false)
      toast.success(isEditing ? 'Vehicle updated successfully' : 'Vehicle created successfully')
    } catch (error) {
      console.error('Error saving vehicle:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to save vehicle')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleRowClick = (row) => {
    setSelectedVehicle(row)
    setDetailOpen(true)
  }

  const handleEdit = () => {
    setFormData({
      registration_number: selectedVehicle.registration_number || '',
      name: selectedVehicle.name || '',
      vehicle_type: selectedVehicle.vehicle_type || 'car',
      make: selectedVehicle.make || '',
      model: selectedVehicle.model || '',
      year: selectedVehicle.year || new Date().getFullYear(),
      color: selectedVehicle.color || '',
      vin: selectedVehicle.vin || '',
      engine_number: selectedVehicle.engine_number || '',
      fuel_type: selectedVehicle.fuel_type || 'diesel',
      status: selectedVehicle.status || 'operational',
      department: selectedVehicle.department || '',
      assigned_driver: selectedVehicle.assigned_driver || '',
      purchase_date: selectedVehicle.purchase_date || '',
      purchase_cost: selectedVehicle.purchase_cost || '',
      supplier: selectedVehicle.supplier || '',
      insurance_company: selectedVehicle.insurance_company || '',
      insurance_policy_number: selectedVehicle.insurance_policy_number || '',
      insurance_expiry: selectedVehicle.insurance_expiry || '',
      registration_expiry: selectedVehicle.registration_expiry || '',
      inspection_expiry: selectedVehicle.inspection_expiry || '',
      current_mileage: selectedVehicle.current_mileage || 0,
      next_service_mileage: selectedVehicle.next_service_mileage || '',
      notes: selectedVehicle.notes || '',
    })
    // Set existing image previews
    setImagePreview(selectedVehicle.image || null)
    setImagePreview2(selectedVehicle.image_2 || null)
    setImageFile(null)
    setImageFile2(null)
    setIsEditing(true)
    setDetailOpen(false)
    setFormOpen(true)
  }

  const isExpiringOrExpired = (date) => {
    if (!date) return false
    const expiryDate = new Date(date)
    const today = new Date()
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
    return expiryDate <= thirtyDaysFromNow
  }

  return (
    <Box>
      <PageHeader
        title="Vehicles"
        subtitle="Manage organizational vehicles"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Vehicles' },
        ]}
        actions={
          <PermissionGate permission="vehicles.create">
            <Button startIcon={<Add />} onClick={() => { setIsEditing(false); setFormData(initialFormData); resetImageState(); setFormOpen(true) }}>
              Add Vehicle
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={vehicles}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search vehicles..."
          emptyMessage="No vehicles found"
        />
      </Card>

      {/* Create/Edit Vehicle Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Registration Number"
                name="registration_number"
                value={formData.registration_number}
                onChange={handleChange}
                error={Boolean(errors.registration_number)}
                helperText={errors.registration_number}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Vehicle Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  label="Vehicle Type"
                >
                  {VEHICLE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Make"
                name="make"
                value={formData.make}
                onChange={handleChange}
                error={Boolean(errors.make)}
                helperText={errors.make}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                error={Boolean(errors.model)}
                helperText={errors.model}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Color"
                name="color"
                value={formData.color}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="VIN"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Engine Number"
                name="engine_number"
                value={formData.engine_number}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Fuel Type</InputLabel>
                <Select
                  name="fuel_type"
                  value={formData.fuel_type}
                  onChange={handleChange}
                  label="Fuel Type"
                >
                  {FUEL_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Assignment */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Assignment
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Assigned Driver</InputLabel>
                <Select
                  name="assigned_driver"
                  value={formData.assigned_driver}
                  onChange={handleChange}
                  label="Assigned Driver"
                >
                  <MenuItem value="">None</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Insurance & Registration */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Insurance & Registration
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Insurance Company"
                name="insurance_company"
                value={formData.insurance_company}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Insurance Policy #"
                name="insurance_policy_number"
                value={formData.insurance_policy_number}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Insurance Expiry"
                name="insurance_expiry"
                type="date"
                value={formData.insurance_expiry}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Registration Expiry"
                name="registration_expiry"
                type="date"
                value={formData.registration_expiry}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Inspection Expiry"
                name="inspection_expiry"
                type="date"
                value={formData.inspection_expiry}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Service Info */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Service Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Current Mileage"
                name="current_mileage"
                type="number"
                value={formData.current_mileage}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Next Service Mileage"
                name="next_service_mileage"
                type="number"
                value={formData.next_service_mileage}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                }}
              />
            </Grid>

            {/* Purchase Info */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Purchase Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Purchase Date"
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Purchase Cost"
                name="purchase_cost"
                type="number"
                value={formData.purchase_cost}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleChange}
                  label="Supplier"
                >
                  <MenuItem value="">None</MenuItem>
                  {suppliers.map((sup) => (
                    <MenuItem key={sup.id} value={sup.id}>
                      {sup.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Images Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Vehicle Images
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  position: 'relative',
                  minHeight: 150,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {imagePreview ? (
                  <>
                    <Box
                      component="img"
                      src={imagePreview}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 120,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(1)}
                      sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Primary Image
                    </Typography>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 1)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  position: 'relative',
                  minHeight: 150,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {imagePreview2 ? (
                  <>
                    <Box
                      component="img"
                      src={imagePreview2}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 120,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(2)}
                      sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Secondary Image
                    </Typography>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, 2)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Vehicle Details
          {selectedVehicle && (
            <Chip
              label={selectedVehicle.status?.replace('_', ' ').toUpperCase()}
              color={STATUS_COLORS[selectedVehicle.status] || 'default'}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedVehicle && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar
                      src={selectedVehicle.image}
                      variant="rounded"
                      sx={{ width: 100, height: 80 }}
                    >
                      <DirectionsCar sx={{ fontSize: 40 }} />
                    </Avatar>
                    {selectedVehicle.image_2 && (
                      <Avatar
                        src={selectedVehicle.image_2}
                        variant="rounded"
                        sx={{ width: 100, height: 80 }}
                      >
                        <DirectionsCar sx={{ fontSize: 40 }} />
                      </Avatar>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6">{selectedVehicle.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedVehicle.vehicle_number} | {selectedVehicle.registration_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography>{selectedVehicle.vehicle_type?.replace('_', ' ')}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Color</Typography>
                <Typography>{selectedVehicle.color || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Fuel Type</Typography>
                <Typography>{selectedVehicle.fuel_type}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Current Mileage</Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Speed fontSize="small" />
                  {selectedVehicle.current_mileage?.toLocaleString() || 0} km
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Department</Typography>
                <Typography>{selectedVehicle.department_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Assigned Driver</Typography>
                <Typography>{selectedVehicle.assigned_driver_name || 'Unassigned'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">VIN</Typography>
                <Typography>{selectedVehicle.vin || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Insurance & Registration</Typography>
              </Grid>

              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Insurance Company</Typography>
                <Typography>{selectedVehicle.insurance_company || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Insurance Expiry</Typography>
                <Typography color={isExpiringOrExpired(selectedVehicle.insurance_expiry) ? 'error' : 'inherit'}>
                  {selectedVehicle.insurance_expiry ? new Date(selectedVehicle.insurance_expiry).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Registration Expiry</Typography>
                <Typography color={isExpiringOrExpired(selectedVehicle.registration_expiry) ? 'error' : 'inherit'}>
                  {selectedVehicle.registration_expiry ? new Date(selectedVehicle.registration_expiry).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>

              {selectedVehicle.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography>{selectedVehicle.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          <PermissionGate permission="vehicles.edit">
            <Button onClick={handleEdit}>
              Edit
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VehiclesList
